/**
 * Get all the cookies
 * @returns {(Array.<{{key: string, value: string}}>|Array)}
 */
export const getCookies = () => {
    if (document.cookie) {
        return document.cookie.split('; ').map((cookie) => {
            const parts = cookie.split('=');
            return { key: parts[0], value: parts[1] };
        });
    }

    return [];
};

/**
 * Get cookie by name
 * @param key cookie name
 * @returns {null|*|string}
 */
export const getCookie = (key) => {
    if (!key) {
        return null;
    }

    try {
        return getCookies().find((cookie) => cookie.key === key).value;
    } catch (error) {
        return null;
    }
};

/**
 * Set cookie
 * @param {string} key cookie name
 * @param {string} value cookie value
 * @param {{}} attributes for possible attributes see
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie#write_a_new_cookie
 *
 * @returns {void}
 */
export const setCookie = (key, value, attributes = {}) => {
    if (!key || typeof value === 'undefined') {
        return;
    }

    let cookie = `${key}=${value}`;
    /* eslint-disable */
    for (const [attributeKey, attributeValue] of Object.entries(attributes)) {
        cookie += `; ${attributeKey}=${attributeValue}`;
    }
    /* eslint-enable */
    document.cookie = cookie;
};

/**
 * Set more than one cookie at the time
 *
 * @param {Object[]} cookies - An array of objects with `key` and `value` properties. `attributes` prop is optional
 * @param {string} cookies[].key - The key of cookie
 * @param {string} cookies[].value - The value of cookie
 * @param {Object} [cookies[].attributes] - The cookie's attributes
 * @param {Array.<{Object.<string, string>}>}
 *
 * @returns {void}
 */
export const setCookies = (cookies) => {
    if (!Array.isArray(cookies)) {
        return;
    }

    cookies.forEach(({ key, value, attributes = {} }) => {
        setCookie(key, value, attributes);
    });
};

/**
 * Delete cookie
 * @param {string} key cookie name
 * @param {string|null} path optional. Needed to delete the right cookie when the cookie's path is different from default
 *
 * @returns {void}
 */
export const deleteCookie = (key, path = null) => {
    if (!key) {
        return;
    }

    const attributes = { expires: 'Thu, 01 Jan 1970 00:00:00 UTC' };

    if (typeof path === 'string') {
        attributes.path = path;
    }

    setCookie(key, '', attributes);
};

/**
 * Delete more than one cookie at the time
 *
 * @param {Array.<{{key: string, path: string}}>|Array<string>} cookies - Array of
 * items which are either objects with `key` and optional `path` properties or strings which will be treated as cookie's key
 *
 * @returns {void}
 */
export const deleteCookies = function (cookies) {
    if (!Array.isArray(cookies)) {
        return;
    }

    cookies.forEach((cookie) => {
        if (typeof cookie !== 'string') {
            if (typeof cookie === 'object') {
                this.deleteCookie(cookie.key, cookie.path);
            }
        } else {
            this.deleteCookie(cookie);
        }
    });
};
